import {getPixelsFromRem, Show, VStack} from 'platform/foundation';

import {useId, useMemo, useRef} from 'react';
import ReactSelect, {OnChangeValue, type SelectInstance} from 'react-select';

import {defaultTo, isNil, reject} from 'ramda';
import {isNotArray} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {MENU_MAX_HEIGHT} from '../../constants/menuMaxHeight';
import {useComponentsTheme} from '../../hooks/useComponentsTheme';
import {FormControlProps} from '../../types/FormControlProps';
import {OptionTypeBase} from '../../types/OptionTypeBase';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {ChoiceProvider} from './components/ChoiceProvider';
import {ChoiceCommonProps} from './types';
import {selectComponents} from './utils/components';
import {formatOptionFieldLabel} from './utils/formatOptionFieldLabel';
import {getChoiceCSS} from './utils/getChoiceCSS';
import {getFlattenedOptions} from './utils/getFlattenedOptions';
import {reactSelectStyles} from './utils/reactSelectStyles';
import {selectMultiOption} from './utils/selectMultiOption';

export interface MultiChoiceProps<ValueType>
  extends FormControlProps<ValueType[] | null>,
    TestIdProps,
    ChoiceCommonProps<ValueType[], ValueType> {
  'aria-label'?: string;
}

const rejectNil = reject(isNil);

export function MultiChoice<ValueType>(props: MultiChoiceProps<ValueType>) {
  const selectRef = useRef<SelectInstance<OptionTypeBase<ValueType | Nullish>, true> | null>(null);
  const t = useTranslationContext();
  const theme = useComponentsTheme();
  const id = useId();

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const onSelectMount = (
    select: SelectInstance<OptionTypeBase<ValueType | Nullish>, true> | null
  ) => {
    if (select) {
      selectMultiOption(select);
    }

    selectRef.current = select;
  };

  const onMenuOpen = () => {
    if (selectRef.current) {
      selectMultiOption(selectRef.current);
    }
  };

  const handleChange = (options: OnChangeValue<OptionTypeBase<ValueType | Nullish>, true>) => {
    if (!options) {
      props.onChange?.(null);
      return;
    }

    props.onChange?.(rejectNil(options.map((v) => v.value)));
  };

  const getOptionsByValue = (value: ValueType[] | Nullish) => {
    if (isNotArray(value) || isNil(value)) {
      return null;
    }

    const flatOptions = getFlattenedOptions(props.options);

    return rejectNil(value.map((v) => flatOptions?.find((o) => o.value === v)));
  };

  // This is a workaround for an issue in react-select, when using multiselect with custom components
  // https://github.com/JedWatson/react-select/issues/5489
  const components = useMemo(() => selectComponents<ValueType, true>(), []);

  return (
    <div
      onKeyDown={(event) => {
        // We want to stop propagation when the choice is located in a modal, so that closing the option list doesn't close the modal itself.
        if (event.key === 'Escape') {
          event.stopPropagation();
        }
      }}
    >
      <VStack>
        <Label
          id={id}
          isRequired={props.isRequired}
          tooltip={props.tooltip}
          isRecommended={props.isRecommended}
          data-testid={suffixTestId('label', props)}
        >
          {props.label}
        </Label>
        <ChoiceProvider
          options={{
            'data-testid': props['data-testid'],
            hasOptionCheckbox: props.hasOptionCheckbox,
            isInvalid,
          }}
        >
          <ReactSelect
            isMulti
            inputId={id}
            aria-label={props['aria-label']}
            formatOptionLabel={props.formatOptionLabel ?? formatOptionFieldLabel}
            defaultValue={getOptionsByValue(props.defaultValue)}
            ref={onSelectMount}
            menuPortalTarget={props.menuInPortal ? document.body : undefined}
            value={getOptionsByValue(props.value)}
            name={props.name}
            placeholder={defaultTo(t('general.labels.select'), props.placeholder)}
            onInputChange={() => {
              /**
               * workaround
               * https://github.com/JedWatson/react-select/issues/3832
               */
              if (props.menuIsOpen) {
                selectRef?.current?.inputRef?.focus();
              }
            }}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onChange={handleChange}
            isDisabled={props.isDisabled}
            options={props.options}
            isClearable={!props.isNotClearable}
            isSearchable={props.isSearchable ?? true}
            components={components}
            hideSelectedOptions={false}
            tabSelectsValue={false}
            menuPlacement={props.menuPlacement ?? 'auto'}
            maxMenuHeight={getPixelsFromRem(props.maxMenuHeight ?? MENU_MAX_HEIGHT)}
            styles={reactSelectStyles<ValueType, true>(theme)}
            onMenuOpen={onMenuOpen}
            noOptionsMessage={props.noOptionsMessage}
            closeMenuOnScroll={props.closeMenuOnScroll}
            closeMenuOnSelect={props.closeMenuOnSelect}
            isLoading={props.isLoading}
            filterOption={props.filterOption}
            // Styling ReactSelect is problematic so we need to use the css prop
            className={getChoiceCSS(isInvalid, props.isDisabled, props.isRecommended)}
          />
        </ChoiceProvider>
        <Show when={props.errorMessage ?? props.helperText}>
          <HelperText
            errorMessage={props.errorMessage}
            helperText={props.helperText}
            data-testid={suffixTestId('helper', props)}
          />
        </Show>
      </VStack>
    </div>
  );
}
